import { group } from 'd3-array';

import type { ScreenerReq, ScreenerRespEntity } from '@revelio/data-access';
import { GET_SCREENER_DATA } from '@revelio/data-access';
import type { Item } from '@revelio/iso-utility';
import { SelectionCategories } from '@revelio/iso-utility';

import { useAllSelectionLists } from '../../filters/hooks/useAllSelectionLists';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { FilterId, type PrimaryFilterId } from '../../filters/types/filters';
import { hasPrimaryFilter } from '../../filters/utils/common';
import { buildScreenerFilters } from '../../filters/utils/get-screener-filters';
import type { ScreenerChartType } from '../PageConfigProvider/types/chart';
import type { ScreenerMetricValues } from '../PageConfigProvider/types/metric';
import { DEFAULT_SCREENER_PAGINATION_LIMIT } from './constants';
import type { MultiMetricProviderResult } from './MultiMetricProvider';
import {
  type BreakdownSingleDataSet,
  type DataProviderChildren,
  DataProviderQueryStatus,
  DataType,
  type SingleDataPoint,
  type SingleDataSet,
} from './types';
import { useUrqlQueryWithStatus } from './utils/data-provider-results-utils';
import {
  processMultipleScreenerMetrics,
  processScreenerEntityData,
} from './utils/screener-metrics-utils';
import {
  type ScreenerTableRow,
  transformScreenerDataForTable,
} from './utils/transform-screener-table-data';

type SingleMetricProps = {
  primaryDimension: PrimaryFilterId;
  metric: ScreenerMetricValues;
  chartType?: ScreenerChartType;
  children: DataProviderChildren<
    SingleDataSet | BreakdownSingleDataSet | undefined
  >;
};

type MultiMetricProps = {
  primaryDimension: PrimaryFilterId;
  metrics: ScreenerMetricValues[];
  children: DataProviderChildren<MultiMetricProviderResult>;
};

type ScreenerTableProviderProps = {
  primaryDimension: PrimaryFilterId;
  metrics: ScreenerMetricValues[];
  pagination?: { offset: number; limit: number };
  sorting?: ScreenerReq['sorting'];
  children: DataProviderChildren<{
    data: ScreenerTableRow[];
    totalCount: number;
  }>;
};

type ScreenerTableDataProviderProps = SingleMetricProps | MultiMetricProps;

const isSingleMetricProps = (
  props: ScreenerTableDataProviderProps
): props is SingleMetricProps => {
  return 'metric' in props;
};

export const ScreenerDataProvider = (props: ScreenerTableDataProviderProps) => {
  if (isSingleMetricProps(props)) {
    return <SingleScreenerProvider {...props} />;
  }
  return <MultiScreenerProvider {...props} />;
};

const SingleScreenerProvider = ({
  primaryDimension,
  metric,
  chartType,
  children,
}: SingleMetricProps) => {
  const effectiveFilters = useEffectiveFilters();

  const isPrimaryFilterSet = hasPrimaryFilter({
    primaryDimension,
    filters: effectiveFilters,
  });

  const variables = {
    filters: buildScreenerFilters({
      effectiveFilters,
      primaryDimension,
      metric,
      pagination: { limit: DEFAULT_SCREENER_PAGINATION_LIMIT, offset: 0 },
    }),
  };

  const [{ data, status }] = useUrqlQueryWithStatus<
    { screener: { entities: ScreenerRespEntity[] } },
    { filters: ScreenerReq }
  >({
    query: GET_SCREENER_DATA,
    variables,
    pause: !isPrimaryFilterSet,
  });

  const entities = data?.screener?.entities ?? [];

  const rows = processScreenerEntityData(entities, metric);

  const selectionLists = useAllSelectionLists();
  const msaList = selectionLists.find((l) => l.id === SelectionCategories.MSA);
  const countryList = selectionLists.find(
    (l) => l.id === SelectionCategories.COUNTRY
  );

  const msaIdToCountryId = new Map<string, string>();
  (msaList?.value || []).forEach((item: Item) => {
    // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
    const withCountry = (item as any).country ?? item.parentId;
    let parentId: string | undefined;
    if (Array.isArray(withCountry) && withCountry.length > 0) {
      parentId = String(withCountry[0]);
    } else if (
      typeof withCountry === 'string' ||
      typeof withCountry === 'number'
    ) {
      parentId = String(withCountry);
    }
    if (!parentId) return;
    msaIdToCountryId.set(String(item.id), parentId);
  });

  const countryIdToName = new Map<string, string>();
  (countryList?.value || []).forEach((item) => {
    const name =
      item.longName || item.shortName || item.label || String(item.id);
    countryIdToName.set(String(item.id), String(name));
  });

  const converted: SingleDataSet | BreakdownSingleDataSet | undefined =
    status === DataProviderQueryStatus.Success
      ? (() => {
          if (
            chartType === 'treemap' &&
            primaryDimension === FilterId.GEOGRAPHY
          ) {
            const withCountry = entities.map((e) => {
              const msaId = String(e.entity_id);
              const countryId = msaIdToCountryId.get(msaId) || 'Unknown';
              const countryLabel =
                countryIdToName.get(countryId) ||
                String(countryId) ||
                'Unknown';
              const label = e.long_name || e.short_name || '';
              const value =
                processScreenerEntityData([e], metric)[0]?.value ?? 0;
              return { countryLabel, metro: label, value };
            });

            const grouped = group(withCountry, (d) => d.countryLabel);
            const data: BreakdownSingleDataSet['data'] = Array.from(
              grouped,
              ([key, arr]) => ({
                dim: key,
                breakdown: arr.map<SingleDataPoint>((d) => ({
                  dim: d.metro,
                  value: d.value,
                })),
              })
            );
            return { type: DataType.BREAKDOWN_SINGLE, data };
          }
          return { type: DataType.SINGLE, data: rows };
        })()
      : undefined;

  return children({ data: converted, status });
};

const MultiScreenerProvider = ({
  primaryDimension,
  metrics,
  children,
}: MultiMetricProps) => {
  const effectiveFilters = useEffectiveFilters();

  const variables = (() => {
    const firstMetric: ScreenerMetricValues =
      metrics[0] ?? 'screener_headcount';
    const base = buildScreenerFilters({
      effectiveFilters,
      primaryDimension,
      metric: firstMetric,
      pagination: { limit: DEFAULT_SCREENER_PAGINATION_LIMIT, offset: 0 },
    });

    // buildScreenerFilters already creates all the correct segments from filter state.
    // Each filter has its own independent segments (e.g., "Attrition Rate segmented by Ethnicity"
    // only applies to attrition, not to other metrics). No need to copy demographic filters
    // across metrics - that was legacy behavior from the old screener.
    return { filters: base };
  })();

  const [{ data, status }] = useUrqlQueryWithStatus<
    { screener: { entities: ScreenerRespEntity[] } },
    { filters: ScreenerReq }
  >({
    query: GET_SCREENER_DATA,
    variables,
    pause: metrics.length === 0,
  });

  const entities = data?.screener?.entities ?? [];

  const byMetric = (() => {
    const processedData = processMultipleScreenerMetrics(entities, metrics);
    return metrics.reduce<MultiMetricProviderResult['byMetric']>((acc, m) => {
      acc[m] = { type: DataType.SINGLE, data: processedData[m] } as const;
      return acc;
    }, {});
  })();

  return children({
    data: { byMetric },
    status: status ?? DataProviderQueryStatus.Idle,
  });
};

export const ScreenerTableDataProvider = ({
  primaryDimension,
  metrics,
  pagination = { offset: 0, limit: DEFAULT_SCREENER_PAGINATION_LIMIT },
  sorting,
  children,
}: ScreenerTableProviderProps) => {
  const effectiveFilters = useEffectiveFilters();

  const variables = (() => {
    const firstMetric: ScreenerMetricValues =
      metrics[0] ?? 'screener_headcount';
    const base = buildScreenerFilters({
      effectiveFilters,
      primaryDimension,
      metric: firstMetric,
      pagination,
      sorting,
    });

    return { filters: base };
  })();

  const shouldPauseQuery =
    metrics.length === 0 ||
    (Object.keys(variables.filters.primary_selector || {}).length === 0 &&
      Object.keys(variables.filters.segments || {}).length === 0 &&
      Object.keys(variables.filters.entity_info_selectors || {}).length === 0);

  const [{ data, status }] = useUrqlQueryWithStatus<
    {
      screener: {
        entities: ScreenerRespEntity[];
        pagination: { total_results: number };
      };
    },
    { filters: ScreenerReq }
  >({
    query: GET_SCREENER_DATA,
    variables,
    pause: shouldPauseQuery,
  });

  const entities = data?.screener?.entities ?? [];
  const totalCount = data?.screener?.pagination?.total_results ?? 0;

  const transformedData = transformScreenerDataForTable(
    entities,
    variables.filters,
    effectiveFilters
  );

  return children({
    data: { data: transformedData, totalCount },
    status: shouldPauseQuery
      ? DataProviderQueryStatus.Success
      : (status ?? DataProviderQueryStatus.Idle),
  });
};
